import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Package, AlertTriangle, RefreshCw, Plus, Search, TrendingDown } from "lucide-react";
import { PageHeader }  from "@/components/shared/PageHeader";
import { StatCard }    from "@/components/shared/StatCard";
import { useInventario, AlertaBadge, AlertasPanel, AjusteModal } from "@/features/inventory";
import { calcularAlerta } from "@cold-crew/shared";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/inventario/")({
  component: InventarioPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCantidad(val: number, unidad: string) {
  if (unidad === "g"  && val >= 1000) return `${(val / 1000).toFixed(2)} kg`;
  if (unidad === "ml" && val >= 1000) return `${(val / 1000).toFixed(2)} L`;
  return `${val} ${unidad}`;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function InventarioPage() {
  const [search,      setSearch]     = useState("");
  const [soloAlertas, setSoloAlertas] = useState(false);
  const [ajusteOpen,  setAjusteOpen]  = useState<{ id: number; nombre: string; unidad: string } | null>(null);

  const { data, isLoading, isError, refetch, isFetching } = useInventario({
    search: search || undefined,
    solo_alertas: soloAlertas || undefined,
  });

  const rows = data?.data ?? [];

  const totalProductos = rows.length;
  const criticos  = rows.filter((r) => r.alerta === "rojo").length;
  const bajos     = rows.filter((r) => r.alerta === "amarillo").length;
  const sinStock  = rows.filter((r) => r.cantidad_actual === 0).length;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Inventario"
        description={`${totalProductos} producto${totalProductos !== 1 ? "s" : ""}`}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => refetch()} disabled={isFetching}
              className="p-2 rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-50" title="Actualizar">
              <RefreshCw className={cn("size-4", isFetching && "animate-spin")} />
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Productos"   value={totalProductos} icon={Package} />
        <StatCard label="Sin stock"   value={sinStock}       icon={TrendingDown} />
        <StatCard label="Bajos"       value={bajos}          icon={AlertTriangle} />
        <StatCard label="Críticos"    value={criticos}       icon={AlertTriangle}
          className={criticos > 0 ? "border-red-200 dark:border-red-900" : undefined} />
      </div>

      {/* Alertas panel */}
      {(criticos > 0 || bajos > 0) && <AlertasPanel />}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
          <input type="search" placeholder="Buscar producto…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-8 pr-3 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring w-52 transition-colors" />
        </div>
        <button onClick={() => setSoloAlertas((v) => !v)}
          className={cn(
            "flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm transition-colors",
            soloAlertas
              ? "bg-yellow-50 border-yellow-300 text-yellow-700 dark:bg-yellow-950/30 dark:border-yellow-700 dark:text-yellow-400"
              : "border-border hover:bg-muted text-muted-foreground"
          )}>
          <AlertTriangle className="size-3.5" />
          Solo alertas
        </button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm gap-2">
            <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Cargando inventario…
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p className="text-sm text-destructive">Error al cargar inventario.</p>
            <button onClick={() => refetch()} className="text-sm text-primary hover:underline">Reintentar</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
            <Package className="size-8 opacity-40" strokeWidth={1.25} />
            <p className="text-sm">Sin productos en inventario</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  {["Producto", "Categoría", "Stock actual", "Mínimo", "Estado", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((row) => {
                  const alerta = row.alerta ?? calcularAlerta(row.cantidad_actual, row.cantidad_minima);
                  return (
                    <tr key={row.id} className={cn(
                      "hover:bg-muted/40 transition-colors group",
                      alerta === "rojo"     && "bg-red-50/30 dark:bg-red-950/10",
                      alerta === "amarillo" && "bg-yellow-50/30 dark:bg-yellow-950/10",
                    )}>
                      <td className="px-4 py-3 font-medium text-foreground">
                        {row.producto?.nombre ?? `Producto #${row.producto_id}`}
                        {row.producto?.descripcion && (
                          <span className="block text-xs text-muted-foreground font-normal truncate max-w-[200px]">{row.producto.descripcion}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 capitalize text-muted-foreground">{row.producto?.categoria ?? "—"}</td>
                      <td className="px-4 py-3 tabular-nums font-semibold">
                        <span className={cn(
                          alerta === "rojo"     && "text-red-600 dark:text-red-400",
                          alerta === "amarillo" && "text-yellow-700 dark:text-yellow-400",
                          alerta === "ok"       && "text-foreground",
                        )}>
                          {fmtCantidad(row.cantidad_actual, row.producto?.unidad ?? "")}
                        </span>
                      </td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">
                        {fmtCantidad(row.cantidad_minima, row.producto?.unidad ?? "")}
                      </td>
                      <td className="px-4 py-3">
                        <AlertaBadge nivel={alerta as any} />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setAjusteOpen({
                            id:     row.producto_id,
                            nombre: row.producto?.nombre ?? `Producto #${row.producto_id}`,
                            unidad: row.producto?.unidad ?? "pz",
                          })}
                          className="opacity-0 group-hover:opacity-100 flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-border hover:bg-muted transition-all"
                        >
                          <Plus className="size-3" />
                          Ajustar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ajuste modal */}
      {ajusteOpen && (
        <AjusteModal
          productoId={ajusteOpen.id}
          productoNombre={ajusteOpen.nombre}
          unidad={ajusteOpen.unidad}
          onClose={() => setAjusteOpen(null)}
        />
      )}
    </div>
  );
}
